import { AgentCategory } from "~/shared/enums";
import type { AgentConfig, ValidationFunctionResult } from "~/shared/types";
import { makeValidateRegex } from "~/utils/validation";

import AgentIcon from "./AgentIcon.vue";
import Icon from "./Icon.vue";

const API_KEY_REGEX = /^sk-ant-[A-Za-z0-9_-]+$/;
const AGENT_ID_REGEX = /^agent_[0-9A-Za-z_-]+$/;
const ENVIRONMENT_ID_REGEX = /^env_[0-9A-Za-z_-]+$/;
const VAULT_ID_REGEX = /^vlt_[0-9A-Za-z_-]+$/;

const validateApiKey = makeValidateRegex(API_KEY_REGEX, "Enter a valid Anthropic API key", true);
const validateAgentId = makeValidateRegex(AGENT_ID_REGEX, "Enter a valid agent ID", true);
const validateEnvironmentId = makeValidateRegex(ENVIRONMENT_ID_REGEX, "Enter a valid environment ID", true);
const validateVaultIds = (value: string): ValidationFunctionResult => {
  if (value.trim() === "") {
    return { isValid: false, error: "Enter a value" };
  }
  const items = value
    .split(/[,\n]/)
    .map((s) => s.trim())
    .filter((s) => s !== "");
  return items.every((item) => VAULT_ID_REGEX.test(item))
    ? { isValid: true }
    : { isValid: false, error: "Enter valid vault IDs starting with vlt_" };
};

const config: AgentConfig = {
  name: "Claude",
  category: AgentCategory.ASSISTANT,
  icon: Icon,
  agentIcon: AgentIcon,
  color: "#d97757",
  description: "Use Claude for deep reasoning, planning, research, and technical drafting",
  descriptionMarkdown: `## Overview

Assign a task to Claude and Dart will start a Managed Agents session with the Anthropic agent and environment you already configured. Claude replies back in the task, and follow-up comments continue the same managed-agent session instead of starting over.

## Setup guide

1. Create an Anthropic API key with Claude Managed Agents access
2. Create or choose an existing Anthropic managed agent, environment, and vaults by following the [Claude agent quickstart](https://platform.claude.com/workspaces/default/agent-quickstart)
3. Open Dart settings, go to Agents, and choose Claude
4. Paste your Anthropic API key, agent ID, environment ID, and vault IDs
5. Assign a task to Claude from Dart`,
  configFields: [
    {
      key: "apiKey",
      type: "password",
      label: "Anthropic API key",
      placeholder: "Your Anthropic API key",
      validate: validateApiKey,
    },
    {
      key: "agentId",
      type: "text",
      label: "Agent ID",
      placeholder: "agent_..",
      validate: validateAgentId,
    },
    {
      key: "environmentId",
      type: "text",
      label: "Environment ID",
      placeholder: "env_..",
      validate: validateEnvironmentId,
    },
    {
      key: "vaultIds",
      type: "text",
      label: "Vault IDs (comma or newline separated)",
      placeholder: "vlt_123, vlt_456",
      validate: validateVaultIds,
    },
  ],
};

export default config;
